import { Injectable, Injector, WritableSignal } from '@angular/core';
import { ILookupService, LookupApi, LookupDetails } from '@via/models';
import { BehaviorSubject } from 'rxjs';
import { ApiService } from 'src/app/services/api.service';

import { PascalToSpacePipe } from '../pipes/pascal-to-space.pipe';

import { CommonService } from './common.service';
import { LoggerService as logger } from './logger.service';

@Injectable({
  providedIn: 'root',
})
export class LookupService<TResponse, T>
  implements ILookupService<TResponse, T>
{
  // Injected Services
  protected apiService = this.injector.get(ApiService);
  protected commonService = this.injector.get(CommonService);
  protected pascalToSpacePipe = this.injector.get(PascalToSpacePipe);

  protected lookupApi: LookupApi = LookupApi.Core;
  protected lookups!: WritableSignal<TResponse>;

  // If lookups are empty then call API for maximum of 3 times
  private failureCount = 0;
  // This is used due to computed being called internally when updating lookup object
  private inLoadingQueue = new Set<string>();

  public lookup$ = new BehaviorSubject<boolean>(false);

  constructor(private injector: Injector) {}

  async fetchLookups() {
    try {
      const lookupRes = await this.apiService.get<TResponse>(
        `${this.lookupApi}/api/lookups`
      );

      this.lookups.set(lookupRes);
    } catch (e) {
      logger.error(e);
      this.commonService.showToast('Error', "Couldn't fetch lookups!");
    }
  }

  async fetchLookupsByName(name: string) {
    try {
      if (this.inLoadingQueue.has(name) || this.failureCount >= 3) {
        return;
      }
      this.inLoadingQueue.add(name);

      const lookupRes = await this.apiService.get<TResponse>(
        `${this.lookupApi}/api/lookups`,
        {
          params: {
            names: [name],
          },
        }
      );

      if (!(lookupRes[name as keyof TResponse] as T[])?.length) {
        this.failureCount++;
      }

      this.lookups.update((value) => ({
        ...value,
        ...lookupRes,
      }));

      this.inLoadingQueue.delete(name);

      if (!this.inLoadingQueue.size) {
        this.lookup$.next(true);
      }
    } catch (e) {
      logger.error(e);
      this.commonService.showToast('Error', "Couldn't fetch lookups!");
    }
  }

  fetchDetailedLookupsByName<T = LookupDetails>(name: string) {
    return this.apiService.get<{ [name: string]: T[] }>(
      `${this.lookupApi}/api/lookups`,
      {
        params: {
          names: [name],
          isDetail: true,
        },
      }
    );
  }

  clearCache() {
    return this.apiService.get<void>(
      `${this.lookupApi}/api/lookups/clear-cache`
    );
  }
}
